import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Radar, Plus, Pencil, Copy, Power, Trash2, MapPin, Clock, Loader2, Play, CalendarClock, Sparkles, Activity,
} from "lucide-react";
import api, { apiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";

const STATUS_STYLE = {
  active: "bg-emerald-100 text-emerald-700 border-emerald-200",
  draft: "bg-amber-100 text-amber-700 border-amber-200",
  inactive: "bg-slate-100 text-slate-600 border-slate-200",
};
const TZ_ABBR = {
  "America/Los_Angeles": "PT", "America/Denver": "MT", "America/Phoenix": "MST",
  "America/Chicago": "CT", "America/New_York": "ET",
};
const WD = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const fmtTime = (t) => {
  const [h, m] = (t || "05:00").split(":").map(Number);
  const ap = h >= 12 ? "PM" : "AM";
  const hh = h % 12 || 12;
  return `${hh}:${String(m).padStart(2, "0")} ${ap}`;
};
const scheduleText = (s) => {
  if (!s || !s.enabled) return "Not scheduled";
  const tz = TZ_ABBR[s.timezone] || s.timezone || "";
  if (s.frequency === "weekly") {
    const days = (s.weekdays || []).sort((a, b) => a - b).map((d) => WD[d]).join(", ") || "—";
    return `Weekly · ${days} · ${fmtTime(s.time)} ${tz}`;
  }
  return `Daily · ${fmtTime(s.time)} ${tz}`;
};
const relTime = (iso) => {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export default function AIBuyBoxes() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);
  const pollRef = useRef(null);
  const [activity, setActivity] = useState(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get("/acq/activity");
        if (alive) setActivity(data);
      } catch { /* ignore */ }
    };
    tick();
    const t = setInterval(tick, 4000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const load = async () => {
    try {
      const { data } = await api.get("/ai-buybox");
      setItems(data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Poll while any box is actively running a discovery.
  useEffect(() => {
    const anyRunning = items.some((b) => b.last_run_status === "running");
    if (anyRunning && !pollRef.current) {
      pollRef.current = setInterval(load, 15000);
    } else if (!anyRunning && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [items]);

  const runNow = async (bb) => {
    try {
      await api.post(`/ai-buybox/${bb.id}/run`);
      toast.success("Sourcing land from your data providers — new leads will appear shortly");
      setItems((prev) => prev.map((x) => (x.id === bb.id ? { ...x, last_run_status: "running" } : x)));
    } catch (e) { toast.error(apiError(e)); }
  };
  const toggleStatus = async (bb) => {
    const next = bb.status === "active" ? "inactive" : "active";
    try {
      await api.post(`/ai-buybox/${bb.id}/status`, { status: next });
      toast.success(next === "active" ? "AI Buy Box activated" : "AI Buy Box deactivated");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const duplicate = async (bb) => {
    try { await api.post(`/ai-buybox/${bb.id}/duplicate`); toast.success("Duplicated"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/ai-buybox/${toDelete.id}`); toast.success("Deleted"); setToDelete(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const markets = (bb) => {
    const g = bb.geography || {};
    const parts = [...(g.cities || []), ...(g.counties || []), ...(g.zips || [])];
    return parts.length ? parts.slice(0, 3).join(" · ") + (parts.length > 3 ? ` +${parts.length - 3}` : "") : (g.state || "No markets set");
  };

  return (
    <div className="p-6 md:p-8 max-w-6xl mx-auto" data-testid="ai-buy-boxes-page">
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2 text-muted-foreground text-sm mb-1">
            <Radar className="h-4 w-4" /> AI Command Center
          </div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight">AI Buy Box</h1>
          <p className="text-muted-foreground mt-1 text-sm">
            Define your target markets and criteria — each run pulls real land from RealEstateAPI (active) +
            PropertyRadar (off-market), verifies parcels against county GIS, and drops the verified matches
            into your Leads list. Runs on demand or on a schedule.
          </p>
        </div>
        <Button data-testid="create-ai-buy-box-btn" onClick={() => nav("/ai-buybox/new")} className="shrink-0">
          <Plus className="h-4 w-4 mr-1.5" /> New AI Buy Box
        </Button>
      </div>

      {activity?.active && (
        <div className="mb-6 rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4" data-testid="live-activity-panel">
          <div className="flex items-center gap-2 mb-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            <span className="text-sm font-semibold text-emerald-400" data-testid="live-activity-title">
              Sourcing land parcels from your data providers…
            </span>
            <Activity className="h-3.5 w-3.5 text-emerald-400/70 ml-auto" />
          </div>
          <div className="text-xs text-muted-foreground">
            Verifying candidates against county GIS. Confirmed matches will appear in your Leads list.
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center border-dashed" data-testid="ai-buy-box-empty">
          <Radar className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <h3 className="text-lg font-medium">No AI Buy Boxes yet</h3>
          <p className="text-muted-foreground text-sm mt-1 mb-5">
            Create one to define your target markets and criteria, then set a schedule to source land automatically.
          </p>
          <Button data-testid="create-ai-buy-box-empty-btn" onClick={() => nav("/ai-buybox/new")}>
            <Plus className="h-4 w-4 mr-1.5" /> New AI Buy Box
          </Button>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {items.map((bb) => {
            const running = bb.last_run_status === "running";
            return (
              <Card key={bb.id} onClick={() => nav(`/ai-buybox/${bb.id}/edit`)} className="p-5 hover:shadow-md transition-shadow cursor-pointer" data-testid={`ai-buy-box-card-${bb.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h3 className="font-semibold text-lg truncate">{bb.name || "Untitled AI Buy Box"}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-muted-foreground mt-0.5">
                      <MapPin className="h-3.5 w-3.5" /> {markets(bb)}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Badge variant="outline" className={STATUS_STYLE[bb.status] || STATUS_STYLE.draft}>
                      {bb.status || "draft"}
                    </Badge>
                    <Badge
                      variant="outline"
                      data-testid={`ai-buy-box-channel-${bb.id}`}
                      className={bb.deal_channel === "on_market"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : "bg-sky-50 text-sky-700 border-sky-200"}
                    >
                      {bb.deal_channel === "on_market" ? "On-Market" : bb.deal_channel === "both" ? "On + Off-Market" : "Off-Market"}
                    </Badge>
                  </div>
                </div>

                <div className="mt-4 space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                    <span data-testid={`ai-buy-box-schedule-${bb.id}`}>{scheduleText(bb.schedule)}</span>
                    <span className="text-xs px-1.5 py-0.5 rounded bg-muted ml-auto">{bb.leads_per_run || 8}/run</span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Clock className="h-3.5 w-3.5 shrink-0" />
                    {running ? (
                      <span className="inline-flex items-center gap-1.5 text-primary font-medium" data-testid={`ai-buy-box-running-${bb.id}`}>
                        <Loader2 className="h-3 w-3 animate-spin" /> Searching…
                      </span>
                    ) : bb.last_run_at ? (
                      <span data-testid={`ai-buy-box-lastrun-${bb.id}`}>
                        {(bb.last_run_created ?? 0)} new lead{(bb.last_run_created ?? 0) === 1 ? "" : "s"} · {relTime(bb.last_run_at)}
                        {bb.last_run_duplicates ? ` · ${bb.last_run_duplicates} dupes` : ""}
                      </span>
                    ) : (
                      <span>Never run yet</span>
                    )}
                  </div>
                </div>

                {(bb.property_types || []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3">
                    {(bb.property_types || []).slice(0, 3).map((t) => (
                      <span key={t} className="text-xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">{t}</span>
                    ))}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-5" onClick={(e) => e.stopPropagation()}>
                  <Button size="sm" className="flex-1" data-testid={`ai-buy-box-run-${bb.id}`} disabled={running} onClick={() => runNow(bb)}>
                    {running ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Play className="h-3.5 w-3.5 mr-1.5" />} Run Now
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" variant="ghost" data-testid={`ai-buy-box-menu-${bb.id}`}>•••</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem data-testid={`ai-buy-box-duplicate-${bb.id}`} onClick={() => duplicate(bb)}>
                        <Copy className="h-4 w-4 mr-2" /> Duplicate
                      </DropdownMenuItem>
                      <DropdownMenuItem data-testid={`ai-buy-box-toggle-${bb.id}`} onClick={() => toggleStatus(bb)}>
                        <Power className="h-4 w-4 mr-2" /> {bb.status === "active" ? "Deactivate" : "Activate"}
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem className="text-red-600" data-testid={`ai-buy-box-delete-${bb.id}`} onClick={() => setToDelete(bb)}>
                        <Trash2 className="h-4 w-4 mr-2" /> Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                {bb.last_run_summary && !running && (
                  <div className="mt-4 text-xs text-muted-foreground bg-secondary/40 border border-border rounded-md px-3 py-2" data-testid={`ai-buy-box-summary-${bb.id}`}>
                    <div className="flex items-center gap-1.5 font-medium text-foreground/80 mb-1"><Sparkles className="h-3 w-3" /> Last run</div>
                    <p className="line-clamp-4 whitespace-pre-wrap">{bb.last_run_summary}</p>
                  </div>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this AI Buy Box?</AlertDialogTitle>
            <AlertDialogDescription>
              "{toDelete?.name}" will be permanently removed. Leads it already generated are kept.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-ai-buy-box" onClick={doDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
